/**
 * AlertDialog - Untitled UI Alert Dialog Component
 *
 * A centered modal dialog for confirmations and alerts following Untitled UI design patterns.
 * Built with React Aria for full accessibility support.
 *
 * @example
 * // Destructive confirmation
 * <AlertDialog
 *   isOpen={showDelete}
 *   onClose={handleClose}
 *   onConfirm={handleDelete}
 *   variant="error"
 *   title="Delete item"
 *   description="Are you sure? This action cannot be undone."
 *   confirmLabel="Delete"
 *   cancelLabel="Cancel"
 * />
 *
 * @example
 * // Success alert
 * <AlertDialog
 *   isOpen={showSuccess}
 *   onClose={handleClose}
 *   onConfirm={handleContinue}
 *   variant="success"
 *   title="Item created"
 *   description="Your item has been created successfully."
 *   confirmLabel="Continue"
 * />
 *
 * @example
 * // Warning confirmation
 * <AlertDialog
 *   isOpen={showWarning}
 *   onClose={handleClose}
 *   onConfirm={handleProceed}
 *   variant="warning"
 *   title="Unsaved changes"
 *   description="You have unsaved changes. Do you want to continue?"
 *   confirmLabel="Continue"
 *   cancelLabel="Go back"
 * />
 */

import { Dialog, Modal as AriaModal, ModalOverlay, Heading } from 'react-aria-components'
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  InfoCircleIcon,
} from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { Button } from '@/components/base/buttons'

// ============================================================================
// Types
// ============================================================================

export type AlertDialogVariant = 'brand' | 'error' | 'warning' | 'success' | 'gray'

export interface AlertDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean
  /** Callback when the dialog should close */
  onClose: () => void
  /** Callback when the confirm action is triggered */
  onConfirm: () => void
  /** Dialog title */
  title: string
  /** Dialog description/message */
  description?: string
  /** Label for the confirm button */
  confirmLabel?: string
  /** Label for the cancel button */
  cancelLabel?: string
  /** Visual variant - determines icon and button color */
  variant?: AlertDialogVariant
  /** Whether the dialog is in a loading state */
  isLoading?: boolean
  /** Custom icon to display (overrides variant icon) */
  icon?: React.ReactNode
  /** Hide the cancel button for single-action dialogs */
  hideCancelButton?: boolean
  /** Additional class name for the dialog */
  className?: string
}

// ============================================================================
// Variant Configuration
// ============================================================================

interface VariantConfig {
  icon: React.ReactNode
  iconBg: string
  iconColor: string
  confirmButtonColor: 'primary' | 'primary-destructive'
}

const variantConfig: Record<AlertDialogVariant, VariantConfig> = {
  brand: {
    icon: <InfoCircleIcon />,
    iconBg: 'bg-brand-100 dark:bg-brand-900/40',
    iconColor: 'text-brand-600 dark:text-brand-400',
    confirmButtonColor: 'primary',
  },
  error: {
    icon: <AlertTriangleIcon />,
    iconBg: 'bg-error-100 dark:bg-error-900/40',
    iconColor: 'text-error-600 dark:text-error-400',
    confirmButtonColor: 'primary-destructive',
  },
  warning: {
    icon: <AlertTriangleIcon />,
    iconBg: 'bg-warning-100 dark:bg-warning-900/40',
    iconColor: 'text-warning-600 dark:text-warning-400',
    confirmButtonColor: 'primary',
  },
  success: {
    icon: <CheckCircleIcon />,
    iconBg: 'bg-success-100 dark:bg-success-900/40',
    iconColor: 'text-success-600 dark:text-success-400',
    confirmButtonColor: 'primary',
  },
  gray: {
    icon: <AlertCircleIcon />,
    iconBg: 'bg-gray-100 dark:bg-gray-800',
    iconColor: 'text-gray-600 dark:text-gray-400',
    confirmButtonColor: 'primary',
  },
}

// ============================================================================
// FeaturedIcon Component (localized for AlertDialog)
// ============================================================================

interface FeaturedIconProps {
  children: React.ReactNode
  bgClassName: string
  iconClassName: string
}

function FeaturedIcon({ children, bgClassName, iconClassName }: FeaturedIconProps) {
  return (
    <div
      className={cn(
        'flex h-12 w-12 items-center justify-center rounded-full',
        bgClassName
      )}
    >
      <div className={cn('h-6 w-6 [&>svg]:h-full [&>svg]:w-full', iconClassName)}>
        {children}
      </div>
    </div>
  )
}

// ============================================================================
// AlertDialog Component
// ============================================================================

export function AlertDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'brand',
  isLoading = false,
  icon,
  hideCancelButton = false,
  className,
}: AlertDialogProps) {
  const config = variantConfig[variant]

  const handleConfirm = () => {
    onConfirm()
    onClose()
  }

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4',
        'bg-gray-950/70 backdrop-blur-sm',
        'entering:animate-in entering:fade-in-0',
        'exiting:animate-out exiting:fade-out-0'
      )}
    >
      <AriaModal
        className={cn(
          'w-full max-w-sm rounded-xl bg-white shadow-xl outline-none dark:bg-gray-900',
          'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
          'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95',
          className
        )}
      >
        <Dialog className="outline-none">
          {/* Body */}
          <div className="px-6 pb-4 pt-6">
            <div className="flex flex-col items-center gap-4 text-center">
              {/* Featured Icon */}
              <FeaturedIcon bgClassName={config.iconBg} iconClassName={config.iconColor}>
                {icon || config.icon}
              </FeaturedIcon>

              {/* Content */}
              <div className="space-y-2">
                <Heading
                  slot="title"
                  className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                >
                  {title}
                </Heading>
                {description && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {description}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-center gap-3 px-6 pb-6">
            {!hideCancelButton && (
              <Button
                color="secondary"
                onPress={onClose}
                isDisabled={isLoading}
                className="flex-1"
              >
                {cancelLabel}
              </Button>
            )}
            <Button
              color={config.confirmButtonColor}
              onPress={handleConfirm}
              isDisabled={isLoading}
              className="flex-1"
            >
              {isLoading ? 'Loading...' : confirmLabel}
            </Button>
          </div>
        </Dialog>
      </AriaModal>
    </ModalOverlay>
  )
}

export default AlertDialog
